import type { Logger } from "libs/logger";
import { Operation } from "libs/transaction";
import { RecordLoaderApi } from "libs/database";
import { ServerDatabaseApi } from "libs/database/server";

/* -------------------------------------------------------------------------------------------------
 *  ActionContext
 * -------------------------------------------------------------------------------------------------
 */

export type ActionClientContext = {
  environment: {
    loader: RecordLoaderApi;
    logger: Logger;
  };
  currentUserId: string;
  type: "client";
};

export type ActionServerContext = {
  environment: {
    db: ServerDatabaseApi;
    loader: RecordLoaderApi;
    logger: Logger;
  };
  currentUserId: string;
  type: "server";
};

export type ActionContext = ActionClientContext | ActionServerContext;

/* -------------------------------------------------------------------------------------------------
 *  Action
 * -------------------------------------------------------------------------------------------------
 */

export interface Action<P> {
  name: string;
  props: P;
  operations: Operation[];
}

export type PartialAction<P> = Omit<Action<P>, "operations">;

/* -------------------------------------------------------------------------------------------------
 *  ActionCreator
 * -------------------------------------------------------------------------------------------------
 */

export interface ActionCreator<P> {
  name: string;
  action: (props: P, context: ActionContext) => Promise<Action<P>>;
  authorize: (props: P, context: ActionServerContext) => Promise<void>;
}

export function createAction<P>(
  name: string,
  handlers: {
    operations: (props: P, context: ActionContext) => Promise<Operation | Operation[]>;
    authorize: (props: P, context: ActionServerContext) => Promise<void>;
  },
): ActionCreator<P> {
  return {
    name,
    action: async (props, context) => {
      const operations = toArray(await handlers.operations(props, context));

      return {
        name,
        props,
        operations,
      };
    },
    authorize: handlers.authorize,
  };
}

/* -------------------------------------------------------------------------------------------------
 *  Helpers
 * -------------------------------------------------------------------------------------------------
 */

function toArray<T>(itemOrItems: T | T[]): T[] {
  if (Array.isArray(itemOrItems)) {
    return itemOrItems;
  } else {
    return [itemOrItems];
  }
}
